<script setup lang="ts">
  import { avatarText } from '@core/utils/formatters';

  import { truncateText } from '@/filters';
  import type { IFormatAuthTenants } from '@/interfaces/agency/auth';
  import { isPowerRole } from '@/router/guards/permission.guard';
  import { useAuthStore } from '@/stores/agency/auth';
  import { useCommonStore } from '@/stores/agency/common';
  import { useIntegrationStore } from '@/stores/agency/integration';
  import { useAgencyStore } from '@/stores/agency/settings/agency/profile';

  interface GroupedTenant {
    id: number;
    name: string;
    slug: string;
    roles: { roleId: number; roleName: string; roleSlug: string }[];
  }

  const props = defineProps<{ isMinimal?: boolean }>();

  const route = useRoute();
  const authStore = useAuthStore();
  const integrationStore = useIntegrationStore();
  const commonStore = useCommonStore();
  const agencyStore = useAgencyStore();

  const loggedInProfile = computed(() => authStore.getLoggedInUserProfile);
  const role = computed(() => authStore.getCurrentTenantRole || '');
  const currentTenantName = computed(() => agencyStore.agencyProfile?.name || authStore.getCurrentTenantName);
  const currentTenantSlug = computed(() => authStore.getCurrentTenantSlug);
  const currentTenantRole = computed(() => authStore.getCurrentTenantRole);
  const firstName = computed(() => loggedInProfile.value?.fullName?.split(' ')[0] || '');

  const profileRoute = computed(() => {
    const roleName = loggedInProfile.value?.role?.name?.toLowerCase() || '';

    return roleName === 'point of contact' ? '/contact/profile' : '/agency/profile';
  });

  const isPowerUser = computed(() => {
    if ((loggedInProfile.value as any)?.isOwnerViewer) return true;
    const roleSlug = loggedInProfile.value?.role?.slug;

    return isPowerRole(roleSlug);
  });

  onMounted(async () => {
    try {
      const userRole = localStorage.getItem('userRole');
      if (userRole === 'tenant_user') {
        await integrationStore.fetchIntegrations();
        await authStore.fetchLoggedInUserProfile();
        await commonStore.fetchAdministrationSchedule();
      }
    } catch (e: unknown) {
      console.log(e);
    }
  });

  const isLogoutDialogOpen = ref(false);

  const logout = async () => {
    isLogoutDialogOpen.value = false;

    try {
      authStore.logout();
    } catch (e: unknown) {
      console.log(e);
    }
  };

  // 👉 Switch agency
  const isMenuOpen = ref(false);
  const tenantSearch = ref('');
  const expandedTenantId = ref<number | null>(null);

  const groupedTenants = computed<GroupedTenant[]>(() => {
    const tenants = authStore.getFormattedTenants as IFormatAuthTenants[];
    const map = new Map<number, GroupedTenant>();

    tenants?.forEach(tenant => {
      const roleEntry = { roleId: tenant.roleId, roleName: tenant.roleName, roleSlug: tenant.roleSlug };
      const existing = map.get(tenant.id);

      const displayName =
        tenant.slug === currentTenantSlug.value && agencyStore.agencyProfile?.name
          ? agencyStore.agencyProfile.name
          : tenant.name;

      if (existing) {
        existing.roles.push(roleEntry);
      } else {
        map.set(tenant.id, { id: tenant.id, name: displayName, slug: tenant.slug, roles: [roleEntry] });
      }
    });

    return Array.from(map.values());
  });

  const filteredTenants = computed(() => {
    const query = tenantSearch.value.trim().toLowerCase();

    if (!query) return groupedTenants.value;

    return groupedTenants.value.filter(tenant => tenant.name.toLowerCase().includes(query));
  });

  const avatarColor = (name: string) => {
    const palette = ['primary', 'secondary', 'success', 'info', 'warning', 'error'];
    let hash = 0;

    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);

    return palette[Math.abs(hash) % palette.length];
  };

  const isActiveTenant = (slug: string, roleName: string) =>
    currentTenantSlug.value === slug && currentTenantRole.value === roleName;

  const handleTenantSelect = async (tenant: GroupedTenant, roleEntry: { roleId: number; roleName: string }) => {
    if (isActiveTenant(tenant.slug, roleEntry.roleName)) return;

    isMenuOpen.value = false;
    await authStore.changeTenant(tenant.id, roleEntry.roleId);
  };

  const handleTenantRowClick = (tenant: GroupedTenant) => {
    if (tenant.roles.length > 1) {
      expandedTenantId.value = expandedTenantId.value === tenant.id ? null : tenant.id;

      return;
    }

    handleTenantSelect(tenant, tenant.roles[0]);
  };
</script>

<template>
  <div class="profile-trigger d-flex align-center ga-2 cursor-pointer rounded-lg px-2 py-1" @click="isMenuOpen = true">
    <VBadge dot location="bottom right" offset-x="3" offset-y="3" bordered color="success">
      <VAvatar size="36" color="primary" variant="tonal">
        <VImg v-if="loggedInProfile?.image" :src="loggedInProfile?.image.original" />
        <span v-else>{{ avatarText(loggedInProfile?.fullName) }}</span>
      </VAvatar>
    </VBadge>

    <div class="d-none d-sm-flex flex-column profile-trigger-text">
      <span class="text-body-2 font-weight-medium line-height-1">
        {{ truncateText(firstName, 15) }}
        <VTooltip
          v-if="firstName.length > 15"
          open-delay="500"
          activator="parent"
          location="bottom"
          :text="firstName"
        />
      </span>
      <span class="text-caption text-medium-emphasis text-truncate">{{ currentTenantName }}</span>
      <span class="text-caption text-disabled text-truncate">{{ role }}</span>
    </div>

    <VIcon icon="tabler-chevron-down" size="18" class="text-medium-emphasis" />

    <!-- SECTION Menu -->
    <VDialog
      v-model="isMenuOpen"
      width="360"
      height="100%"
      transition="dialog-right-transition"
      no-click-animation
      class="setting-service-drawer"
    >
      <VCard class="h-100 rounded-0 d-flex flex-column profile-drawer-card">
        <VList class="drawer-main-list overflow-y-auto pt-0">
          <!-- 👉 User Avatar & Name -->
          <VListItem class="px-4 py-3">
            <template #prepend>
              <VListItemAction start>
                <VAvatar size="42" color="primary" variant="tonal">
                  <VImg v-if="loggedInProfile?.image" :src="loggedInProfile?.image.original" />
                  <span v-else>{{ avatarText(loggedInProfile?.fullName) }}</span>
                </VAvatar>
              </VListItemAction>
            </template>

            <VListItemTitle class="text-body-1 font-weight-semibold">
              {{ loggedInProfile?.fullName }}
            </VListItemTitle>
            <VListItemSubtitle>{{ currentTenantName }}</VListItemSubtitle>
            <VListItemSubtitle>{{ loggedInProfile?.role?.name }}</VListItemSubtitle>

            <template #append>
              <IconBtn size="small" variant="text" @click="isMenuOpen = false">
                <VIcon icon="tabler-x" size="18" />
              </IconBtn>
            </template>
          </VListItem>

          <VDivider />

          <!-- 👉 Switch agency -->
          <template v-if="groupedTenants.length > 1">
            <div class="d-flex align-center justify-space-between px-4 pt-1 mb-2">
              <span class="text-overline font-weight-semibold text-medium-emphasis">Switch Agency</span>
              <span class="text-caption text-medium-emphasis">{{ groupedTenants.length }} agencies</span>
            </div>

            <div class="px-4 mb-2">
              <VTextField
                v-model="tenantSearch"
                density="compact"
                variant="solo-filled"
                flat
                rounded="lg"
                hide-details
                placeholder="Search agencies..."
                prepend-inner-icon="tabler-search"
              />
            </div>

            <div class="tenant-list px-2 overflow-y-auto">
              <template v-for="tenant in filteredTenants" :key="tenant.id">
                <VListItem
                  class="tenant-item rounded-lg mb-1"
                  :active="tenant.roles.length === 1 && isActiveTenant(tenant.slug, tenant.roles[0].roleName)"
                  color="primary"
                  @click="handleTenantRowClick(tenant)"
                >
                  <template #prepend>
                    <VAvatar size="34" :color="avatarColor(tenant.name)" variant="tonal" class="me-1">
                      <span class="text-body-2 font-weight-medium">{{ avatarText(tenant.name) }}</span>
                    </VAvatar>
                  </template>

                  <VListItemTitle class="text-body-2 font-weight-medium">
                    {{ truncateText(tenant.name, 22) }}
                  </VListItemTitle>
                  <VListItemSubtitle>
                    {{ tenant.roles.length > 1 ? 'Multiple Role' : tenant.roles[0].roleName }}
                  </VListItemSubtitle>

                  <template #append>
                    <VIcon
                      v-if="tenant.roles.length === 1 && isActiveTenant(tenant.slug, tenant.roles[0].roleName)"
                      icon="tabler-check"
                      size="18"
                    />
                    <VIcon
                      v-else-if="tenant.roles.length > 1"
                      :icon="expandedTenantId === tenant.id ? 'tabler-chevron-down' : 'tabler-chevron-right'"
                      size="18"
                    />
                  </template>
                </VListItem>

                <template v-if="tenant.roles.length > 1 && expandedTenantId === tenant.id">
                  <VListItem
                    v-for="roleEntry in tenant.roles"
                    :key="roleEntry.roleId"
                    class="tenant-item rounded-lg mb-1"
                    :active="isActiveTenant(tenant.slug, roleEntry.roleName)"
                    color="primary"
                    @click="handleTenantSelect(tenant, roleEntry)"
                  >
                    <template #prepend>
                      <VAvatar size="34" :color="avatarColor(tenant.name)" variant="tonal" class="me-1">
                        <span class="text-body-2 font-weight-medium">{{ avatarText(tenant.name) }}</span>
                      </VAvatar>
                    </template>

                    <VListItemTitle class="text-body-2 font-weight-medium">
                      {{ truncateText(tenant.name, 22) }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ roleEntry.roleName }}</VListItemSubtitle>

                    <template #append>
                      <VIcon v-if="isActiveTenant(tenant.slug, roleEntry.roleName)" icon="tabler-check" size="18" />
                    </template>
                  </VListItem>
                </template>
              </template>

              <div v-if="filteredTenants.length === 0" class="text-center text-caption text-medium-emphasis py-4">
                No agencies found
              </div>
            </div>

            <VDivider class="my-2" />
          </template>

          <!-- 👉 Profile -->
          <VListItem link :to="profileRoute" class="rounded-lg mx-2 menu-item" @click="isMenuOpen = false">
            <template #prepend>
              <VIcon class="me-2" icon="tabler-user" size="22" />
            </template>
            <VListItemTitle>Profile</VListItemTitle>
          </VListItem>

          <!-- ✅ Show these only for power roles (superadmin/admin) and if NOT minimal -->
          <template v-if="!props.isMinimal && isPowerUser">
            <VListItem
              link
              to="/agency/settings"
              class="rounded-lg mx-2 menu-item"
              :class="route.fullPath.includes('/agency/settings') ? 'v-list-item--active' : ''"
              @click="isMenuOpen = false"
            >
              <template #prepend>
                <VIcon class="me-2" icon="tabler-settings" size="22" />
              </template>
              <VListItemTitle>Settings</VListItemTitle>
            </VListItem>

            <VListItem link to="/agency/change-log" class="rounded-lg mx-2 menu-item" @click="isMenuOpen = false">
              <template #prepend>
                <VIcon class="me-2" icon="tabler-menu-2" size="22" />
              </template>
              <VListItemTitle>Change Log</VListItemTitle>
            </VListItem>

            <VListItem link to="/agency/activity-log" class="rounded-lg mx-2 menu-item" @click="isMenuOpen = false">
              <template #prepend>
                <VIcon class="me-2" icon="tabler-clock-cog" size="22" />
              </template>
              <VListItemTitle>Activity Log</VListItemTitle>
            </VListItem>
          </template>

          <!-- 👉 Events -->
          <VListItem link to="/agency/events" class="rounded-lg mx-2 menu-item" @click="isMenuOpen = false">
            <template #prepend>
              <VIcon class="me-2" icon="tabler-calendar-event" size="22" />
            </template>
            <VListItemTitle>Events</VListItemTitle>
          </VListItem>

          <!-- 👉 Branch Dashboard -->
          <VListItem
            v-if="role?.toLowerCase() === 'superadmin'"
            link
            to="/branch/home"
            target="_blank"
            class="rounded-lg mx-2 menu-item"
            @click="isMenuOpen = false"
          >
            <template #prepend>
              <VIcon class="me-2" icon="tabler-external-link" size="22" />
            </template>
            <VListItemTitle>Branch Dashboard</VListItemTitle>
          </VListItem>

          <!-- Divider -->
          <VDivider class="my-2" />

          <!-- 👉 Support -->
          <VListItem link to="/agency/support" class="rounded-lg mx-2 menu-item" @click="isMenuOpen = false">
            <template #prepend>
              <VIcon class="me-2" icon="tabler-headset" size="22" />
            </template>
            <VListItemTitle>Support Tickets</VListItemTitle>
          </VListItem>
        </VList>

        <VDivider />

        <VList class="pt-2 pb-2">
          <!-- 👉 Logout -->
          <VListItem class="rounded-lg mx-2 menu-item" @click="isLogoutDialogOpen = true">
            <template #prepend>
              <VIcon class="me-2" icon="tabler-logout" size="22" color="error" />
            </template>

            <VListItemTitle class="text-error">Logout</VListItemTitle>
          </VListItem>
        </VList>
      </VCard>
    </VDialog>
    <!-- !SECTION -->

    <!-- 👉 Logout confirmation -->
    <VDialog v-model="isLogoutDialogOpen" max-width="420" @click.stop>
      <VCard>
        <VCardTitle class="d-flex align-center gap-2 pt-5 px-6 mt-3">
          <VIcon icon="tabler-logout" color="error" size="22" />
          Logout
        </VCardTitle>
        <VCardText class="px-6">Are you sure you want to log out?</VCardText>
        <VCardActions class="px-6 pb-5">
          <VSpacer />
          <VBtn variant="outlined" color="secondary" @click="isLogoutDialogOpen = false">Cancel</VBtn>
          <VBtn color="error" @click="logout">Logout</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
  .drawer-main-list {
    flex: 1 1 auto;
    min-block-size: 0;
  }

  .profile-trigger {
    border: 1px solid transparent;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease;

    &:hover {
      border-color: rgba(var(--v-border-color), var(--v-border-opacity));
      background-color: rgba(var(--v-theme-on-surface), 0.06);
    }
  }

  .profile-trigger-text {
    line-height: 1.15;
    max-inline-size: 140px;
  }

  .line-height-1 {
    line-height: 1.15;
  }

  .tenant-list {
    max-block-size: 145px;
  }

  .tenant-item {
    transition: background-color 0.15s ease;

    &:not(.v-list-item--active):hover {
      background-color: rgba(var(--v-theme-on-surface), 0.04);
    }
  }

  .tenant-item.v-list-item--active {
    background-color: rgb(var(--v-theme-primary)) !important;

    :deep(.v-list-item-title),
    :deep(.v-list-item-subtitle),
    :deep(.v-icon) {
      color: rgb(var(--v-theme-on-primary)) !important;
    }

    :deep(.v-avatar) {
      background-color: rgba(255, 255, 255, 20%) !important;

      span {
        color: rgb(var(--v-theme-on-primary)) !important;
      }
    }
  }

  .menu-item {
    transition: background-color 0.15s ease;
  }

  .profile-drawer-card {
    :deep(.v-list) {
      background-color: transparent;
    }
  }
</style>
